import { Encoding } from '@mode-vegas/vegas';
import * as $ from 'jquery';
import { defer, truncate } from 'lodash';

import { ChartDrillDown, ChartEncoding, ChartHierarchy, RenderDataModel } from '@mode/charts/util-vegas';
import { EmbedActionType, EmbedActions } from '@mode/reports/sandbox/contract';
import { ChartTypes, InterActions } from '@mode/shared/contract-common';
import { AnalyticsProps } from '@mode/shared/util-segment';

export namespace ChartMenu {
  export interface Options {
    readonly enableExploration: boolean;
    readonly preventExpandingInteraction: boolean;
    analyticsProps?: AnalyticsProps;
    boundOnEmbedAction?: (args: { $action: EmbedActions }) => void;
    generateUUID?: () => string;
    chartToken?: string;
    reportContext?: boolean;
  }

  export function options(overrides: Partial<Options>): Options {
    return {
      enableExploration: false,
      preventExpandingInteraction: true,
      ...overrides,
    };
  }

  const className = 'chart-context-menu';

  let clickHandler: (event: MouseEvent) => void;
  let scrollHandler: (event: MouseEvent) => void;

  function appendMenu(chart: any, menu: JQuery): void {
    $(document.body).append(menu);
  }

  function removeMenu(chart: any): void {
    const menu = $(document.body).find(`.${className}`);
    menu.remove();
    const chartContainerElems = $(document.body).find('.chart-render');
    if (chartContainerElems.length > 0) {
      chartContainerElems.map((index, chartElem) => {
        if (checkCurrentSelectedPoint(chartElem)) {
          // defer to ensure deselection of point happens after point select event fires
          defer(deselectChartPoints, chartContainerElems);
        } else {
          deselectChartPoints(chartElem);
        }
      });
    }
  }

  function checkCurrentSelectedPoint(chartElem: Element): boolean {
    const highchartsObject: any = $(chartElem).highcharts();
    if (!highchartsObject) {
      return false;
    }

    const selectedPoint = highchartsObject.getSelectedPoints()[0];
    const hoverPoint = highchartsObject.hoverPoint;
    if (!selectedPoint || !hoverPoint) {
      return false;
    } else if (selectedPoint.x === hoverPoint.x && selectedPoint.y === hoverPoint.y) {
      return true;
    }
  }

  function deselectChartPoints(chartElem: Element): void {
    const highchartsObject = $(chartElem).highcharts() as unknown as Highcharts.Chart;
    if (!highchartsObject) {
      return;
    }

    const selectedPoints = highchartsObject.getSelectedPoints();
    selectedPoints.map((point) => {
      point.select();
    });
  }

  function enableTooltips(chart: any): void {
    if (chart && chart.options) {
      chart.options.tooltip.enabled = true;
    }
  }

  function disableTooltips(chart: any): void {
    if (chart && chart.options) {
      chart.tooltip.hide();
      chart.options.tooltip.enabled = false;
    }
  }

  function addClickListener(chart: any): void {
    if (scrollHandler != null) {
      removeClickListener(chart);
    }

    clickHandler = (event) => {
      if ($(event.target).closest(`.${className}`).length === 0) {
        clear(chart);
      }
    };

    document.addEventListener('click', clickHandler, true);
  }

  function removeClickListener(chart: any): void {
    document.removeEventListener('click', clickHandler);
    clickHandler = null;
  }

  function addScrollListener(chart: any): void {
    if (scrollHandler != null) {
      removeScrollListener(chart);
    }

    scrollHandler = (event) => clear(chart);
    document.addEventListener('scroll', scrollHandler, true);
  }

  function removeScrollListener(chart: any): void {
    document.removeEventListener('scroll', scrollHandler);
    scrollHandler = null;
  }

  function hasExploreMenuItem(opts: Options) {
    return opts.enableExploration;
  }

  function hasViewMenuItem(opts: Options) {
    return !opts.preventExpandingInteraction;
  }

  function hasDrilldownMenuItem(model: RenderDataModel, encoding: Encoding) {
    return ChartHierarchy.nextDatePart(model, encoding) !== undefined;
  }

  export function clear(chart: any): void {
    removeClickListener(chart);
    removeScrollListener(chart);
    enableTooltips(chart);
    removeMenu(chart);
  }

  export function hasItems(model: RenderDataModel, encoding: Encoding, opts: Options): boolean {
    const hasExploreItem = hasExploreMenuItem(opts);

    const hasViewItem = hasViewMenuItem(opts);

    const hasDrilldownItem = hasDrilldownMenuItem(model, encoding);

    return hasExploreItem || hasViewItem || hasDrilldownItem;
  }

  export function show(
    dispatch: (item: InterActions.ContextItem) => void,
    action: InterActions.MarkClick,
    chart: any,
    model: RenderDataModel,
    encoding: Encoding,
    opts: Options
  ): void {
    const menu = make(dispatch, action, chart, model, encoding, opts);
    removeMenu(chart);
    disableTooltips(chart);
    addClickListener(chart);
    addScrollListener(chart);
    appendMenu(chart, menu);
  }

  function make(
    dispatch: (item: InterActions.ContextItem) => void,
    action: InterActions.MarkClick,
    chart: any,
    model: RenderDataModel,
    encoding: Encoding,
    opts: Options
  ): JQuery {
    let seriesName = action.event.point.series.userOptions.name;
    const seriesColor = action.target.series.color.color;
    const nextDatePart = ChartHierarchy.nextDatePart(model, encoding);
    const isLowestDatePart = ChartHierarchy.checkLowestDatePart(model, encoding);

    let contextMenuEl = makeMenu(chart, action);
    contextMenuEl = contextMenuEl.append(makeHeader(dispatch, action, chart, seriesName, seriesColor));

    if (hasExploreMenuItem(opts)) {
      contextMenuEl = contextMenuEl.append(
        makeExploreMarkItems(contextMenuEl, action, chart, model, encoding, dispatch, opts)
      );
    }

    if (hasViewMenuItem(opts)) {
      contextMenuEl = contextMenuEl.append(makeViewDataItem(dispatch, action, chart, model, encoding, opts));
    }

    if ((opts.enableExploration || !opts.preventExpandingInteraction) && nextDatePart) {
      contextMenuEl = contextMenuEl.append(makeDivider());
    }

    if (hasDrilldownMenuItem(model, encoding)) {
      contextMenuEl = contextMenuEl.append(
        makeDrilldownItem(dispatch, action, chart, model, encoding, nextDatePart, isLowestDatePart, opts)
      );
    }

    return contextMenuEl;
  }

  function makeMenu(chart: any, action: InterActions.MarkClick, extraClassNames = ''): JQuery {
    return $(`<div class="${className + extraClassNames}"></div>`).css({
      opacity: 1,
      position: 'fixed',
      left: `${action.position.clientX}px`,
      top: `${action.position.clientY - 30}px`,
    });
  }

  function makeDivider(): JQuery {
    return $(`<div class="section-divider"></div>`);
  }

  function makeHeader(
    dispatch: (item: InterActions.ContextItem) => void,
    action: InterActions.MarkClick,
    chart: any,
    seriesName: string,
    seriesColor: string
  ): JQuery {
    let labelText = '';
    if (seriesName === null) {
      labelText = 'null';
    } else {
      labelText = seriesName.length > 25 ? truncate(seriesName, { length: 25 }) : seriesName;
    }
    return $(`
      <div class="section-descriptor">
        <div class="section-color" style="color:${seriesColor}">●</div>
        <div class="value label-section-name">${labelText}</div>
      </div>
    `);
  }

  function makeExploreMarkItems(
    contextMenuEl: JQuery,
    action: InterActions.MarkClick,
    chart: any,
    model: RenderDataModel,
    encoding: Encoding,
    dispatch: (item: InterActions.ContextItem) => void,
    opts: Options
  ): JQuery {
    switch (ChartEncoding.shape(encoding)) {
      case ChartEncoding.Shape.Stacked:
        return makeExploreStackedMenuItems(contextMenuEl, action, chart, model, encoding, dispatch, opts);
      case ChartEncoding.Shape.Grouped:
        return makeExploreGroupedMenuItems(contextMenuEl, action, chart, model, encoding, dispatch, opts);
      case ChartEncoding.Shape.Combo:
        return makeExploreComboMenuItems(contextMenuEl, action, chart, model, encoding, dispatch, opts);
      case ChartEncoding.Shape.Radial:
        return makeExploreRadialMenuItems(contextMenuEl, action, chart, model, encoding, dispatch, opts);
    }
  }

  function makeExploreItemElem(value: string): JQuery {
    value = value.length > 19 ? truncate(value, { length: 19 }) : value;

    return $(`
      <div class="context-menu-link">
        <div class="context-menu-label-wrapper">
          <div class="context-menu-text">
            Explore <div class="value link-section-name">${value}</div>...
          </div>
        </div>
      </div>
    `);
  }

  function makeExploreMarkAction(
    action: InterActions.MarkClick,
    ...filters: Array<ChartTypes.Filter>
  ): InterActions.ExploreMarkItem {
    const { target } = action;
    return { target, filters, type: InterActions.ContextItemType.ExploreMark };
  }

  function makeExploreStackedMenuItems(
    contextMenuEl: JQuery,
    action: InterActions.MarkClick,
    chart: any,
    model: RenderDataModel,
    encoding: Encoding,
    dispatch: (item: InterActions.ContextItem) => void,
    opts: Options
  ): JQuery {
    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;

    const xValue = ChartDrillDown.formatValue(model, encoding, encoding.x[0], action.target.x.value);

    const seriesValue = ChartDrillDown.formatValue(
      model,
      encoding,
      props.color.values[0],
      action.target.series.color.value
    );

    const seriesMenuItem = makeExploreItemElem(seriesValue).one('click', (event) => {
      if (opts.reportContext) {
        dispatchAnalyticsEvent(EmbedActionType.ClickExploreFromHere, opts);
      }
      dispatch(
        makeExploreMarkAction(action, {
          name: props.color.values[0].formula,
          value: action.target.series.color.value,
        })
      );
    });

    return contextMenuEl.append(seriesMenuItem);
  }

  function makeExploreGroupedMenuItems(
    contextMenuEl: JQuery,
    action: InterActions.MarkClick,
    chart: any,
    model: RenderDataModel,
    encoding: Encoding,
    dispatch: (item: InterActions.ContextItem) => void,
    opts: Options
  ): JQuery {
    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;

    const xValue = ChartDrillDown.formatValue(model, encoding, encoding.column[0], action.target.columns[0].value);

    const seriesValue = ChartDrillDown.formatValue(
      model,
      encoding,
      props.color.values[0],
      action.target.series.color.value
    );

    const seriesMenuItem = makeExploreItemElem(seriesValue).one('click', (event) =>
      dispatch(
        makeExploreMarkAction(action, {
          name: props.color.values[0].formula,
          value: action.target.series.color.value,
        })
      )
    );

    return contextMenuEl.append(seriesMenuItem);
  }

  function makeExploreComboMenuItems(
    contextMenuEl: JQuery,
    action: InterActions.MarkClick,
    chart: any,
    model: RenderDataModel,
    encoding: Encoding,
    dispatch: (item: InterActions.ContextItem) => void,
    opts: Options
  ): JQuery {
    return contextMenuEl;
  }

  function makeExploreRadialMenuItems(
    contextMenuEl: JQuery,
    action: InterActions.MarkClick,
    chart: any,
    model: RenderDataModel,
    encoding: Encoding,
    dispatch: (item: InterActions.ContextItem) => void,
    opts: Options
  ): JQuery {
    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;

    const seriesValue = ChartDrillDown.formatValue(
      model,
      encoding,
      props.color.values[0],
      action.target.series.color.value
    );

    const seriesMenuItem = makeExploreItemElem(seriesValue).one('click', (event) =>
      dispatch(
        makeExploreMarkAction(action, {
          name: props.color.values[0].formula,
          value: action.target.series.color.value,
        })
      )
    );

    return contextMenuEl.append(seriesMenuItem);
  }

  function makeDrilldownItem(
    dispatch: (item: InterActions.ContextItem) => void,
    action: InterActions.MarkClick,
    chart: any,
    model: RenderDataModel,
    encoding: Encoding,
    nextTimeScale: string,
    isLowestDatePart: boolean,
    opts: Options
  ): JQuery {
    const label = makeDrilldownLabel(model, encoding, action);
    const labelText = label && label.length > 18 ? truncate(label, { length: 18 }) : label;
    const actionDisabled = isLowestDatePart ? 'disabled' : '';
    const timeScaleTextPart1 = isLowestDatePart ? '' : ' by ';
    const timeScaleTextPart2 = isLowestDatePart ? '' : nextTimeScale;

    return $(`
      <div class="context-menu-link ${actionDisabled}">
        <div class="context-menu-label-wrapper">
          <div class="context-menu-text">
            Drilldown <div class="value link-section-name">${labelText}</div>${timeScaleTextPart1}<div class="value time-scale">${timeScaleTextPart2}</div>
          </div>
        </div>
      </div>
    `).one('click', (event) => {
      if (opts.reportContext) {
        dispatchAnalyticsEvent(EmbedActionType.ClickDrilldownInterval, opts);
      }
      dispatch({
        target: action.target,
        type: InterActions.ContextItemType.Drilldown,
      });
    });
  }

  function makeDrilldownLabel(model: RenderDataModel, encoding: Encoding, action: InterActions.MarkClick): string {
    return ChartDrillDown.formatTargetValue(model, encoding, action.target);
  }

  function makeKeepItem(
    dispatch: (item: InterActions.ContextItem) => void,
    action: InterActions.MarkClick,
    chart: any
  ): JQuery {
    return $(`
      <div class="context-menu-link">
        <div class="context-menu-label-wrapper">
          <div class="context-menu-text">Keep</div>
        </div>
      </div>
    `).one('click', (event) => {
      dispatch({
        type: InterActions.ContextItemType.Keep,
      });
    });
  }

  function makeExcludeItem(
    dispatch: (item: InterActions.ContextItem) => void,
    action: InterActions.MarkClick,
    chart: any
  ): JQuery {
    return $(`
      <div class="context-menu-link">
        <div class="context-menu-label-wrapper">
          <div class="context-menu-text">Exclude</div>
        </div>
      </div>
    `).one('click', (event) => {
      dispatch({
        type: InterActions.ContextItemType.Exclude,
      });
    });
  }

  function makeViewDataItem(
    dispatch: (item: InterActions.ContextItem) => void,
    action: InterActions.MarkClick,
    chart: any,
    model: RenderDataModel,
    encoding: Encoding,
    opts: Options
  ): JQuery {
    let viewDataLabel = 'View Data...';
    // TODO: consider reworking chart-renderer.ts to support using raw formulas to avoid "Measure Names" equality check
    if (action.target.series.color?.name === 'Measure Names' && encoding.value.length > 1) {
      const label = truncate(makeDrilldownLabel(model, encoding, action), { length: 18 });
      viewDataLabel = `View Data for all series in <div class="value link-section-name">${label || 'null'}</div>`;
    }

    return $(`
      <div class="context-menu-link">
        <div class="context-menu-label-wrapper">
          <div class="context-menu-text">${viewDataLabel}</div>
        </div>
      </div>
    `).one('click', (event) => {
      if (opts.reportContext) {
        dispatchAnalyticsEvent(EmbedActionType.ClickViewData, opts);
      }
      dispatch({
        target: action.target,
        type: InterActions.ContextItemType.ViewData,
      });
    });
  }

  function dispatchAnalyticsEvent(eventType: EmbedActionType, opts: Options): void {
    const analyticsProps = opts.analyticsProps;
    analyticsProps.event_id = opts.generateUUID(); // generate new event_id since the existing one is the same as the click_chart_data_series event
    opts.boundOnEmbedAction({
      $action: {
        type: EmbedActionType[eventType],
        chartToken: opts.chartToken,
        props: analyticsProps,
      },
    });
  }
}
